/**
 * One row of a facet-sourced filter control: the entity's label, and how many
 * rows of the list's base scope carry it.
 *
 * The count is the half that makes a facet worth the trip — "Acme 12" tells the
 * user the choice is worth making, where a bare "Acme" only tells them it exists
 * (`docs/design-system/ui-list-pages.md` § Filter Option Sources). It renders
 * through `LocalizedNumberDisplay` for the same reason every other number here
 * does; a hand-written `(${count})` would be an un-localized numeral AND a
 * bidi hazard beside an unknown-language entity name.
 *
 * The label is Class A content — an entity name, whose language is unknown at
 * author time — so it goes through `BidiText`. Imported directly rather than
 * through this directory's barrel, which would be a cycle.
 */
import { BidiText } from './BidiText';
import { LocalizedNumberDisplay } from '../LocalizedNumberDisplay';

export interface FacetOptionRowProps {
  label: string;
  /** Omit (or pass null) where the source produced no count — renders the label alone. */
  count?: number | null;
}

export function FacetOptionRow({ label, count }: FacetOptionRowProps) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1.5">
      <BidiText className="min-w-0 flex-1 truncate">{label}</BidiText>
      {count !== undefined && count !== null && (
        // PARENTHESISED, not merely muted and not right-aligned. Right alignment
        // was tried and cannot work here: `SelectItem` wraps its children in an
        // inline, shrink-to-fit bidi span, so a flex row inside it is only as wide
        // as its content and `flex-1` has nothing to push against — the count
        // lands flush after the label whatever it is told. Flush and unbracketed,
        // "Sales 2" reads as a module NAME ending in a digit; the brackets say
        // "this is a count about the thing on the left" at any alignment.
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          (<LocalizedNumberDisplay value={count} />)
        </span>
      )}
    </span>
  );
}
